import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { isIntegratorAdmin, useIntegratorMe } from "@/lib/me-query";
import { appsVisibleToUser } from "@/lib/subapp-access";

function accessNeededSearch(appKey: string, attemptedPath: string, noModules: boolean): string {
  const params = new URLSearchParams();
  params.set("module", appKey);
  params.set("target", attemptedPath);
  if (noModules) {
    params.set("nomodules", "1");
  }
  return params.toString();
}

/**
 * Hub module gate: redirects to `/access-needed` when the user cannot open `appKey` (from `/api/me`
 * `allowedIntegratorSubapps`), including when they have no modules enabled (`nomodules=1`). Integrator
 * administrators always pass.
 */
export function SubappAccessGuard({
  appKey,
  children,
}: {
  appKey: string;
  children: ReactNode;
}) {
  const location = useLocation();
  const { data: me } = useIntegratorMe();
  if (isIntegratorAdmin(me?.user)) {
    return <>{children}</>;
  }
  const visible = appsVisibleToUser(me);
  const attemptedPath = `${location.pathname}${location.search}`;
  if (visible.length === 0) {
    return <Navigate to={`/access-needed?${accessNeededSearch(appKey, attemptedPath, true)}`} replace />;
  }
  if (!visible.some((app) => app.key === appKey)) {
    return <Navigate to={`/access-needed?${accessNeededSearch(appKey, attemptedPath, false)}`} replace />;
  }
  return <>{children}</>;
}
